import { useInlineStep } from "../../components/design";
import { useContext, useEffect, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { haveValue } from "../../helper/common";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/auth/AuthContext";
import Loader from "../../components/Loader";
import { trackClarityEvent } from "../../Utils/clarityReady";
import constants from "../../Utils/constants";
import {
  Badge,
  Button,
  Card,
  Icon,
  LanguageSwitch,
  Modal,
  OptionCard,
} from "../../components/design";
import "./GuarantorModal.css";

const GuarantorModal = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  usedFor,
  setBankshow,
  reconnectFlow = false,
  isDummySignup,
  setStep,
}) => {
  const inline = useInlineStep();
  const { _t, i18n } = useTranslator();
  const navigate = useNavigate();
  const currentLang = (i18n.language || "en-us").toLowerCase();

  const { loadUserTenant } = useContext(AuthContext);
  const [doItLaterLoading, setDoItLaterLoading] = useState(false);
  const [redirectLoading, setRedirectLoading] = useState(false);

  const openPendingGuarantor = (guarantor) => {
    const params = new URLSearchParams({
      guarantorName: guarantor?.name || "",
    });
    const [firstName = "", ...lastNameParts] = (guarantor?.name || "")
      .trim()
      .split(/\s+/);
    params.set("firstName", firstName);
    if (lastNameParts.length) params.set("lastName", lastNameParts.join(" "));
    if (guarantor?.email) {
      params.set("email", guarantor.email);
    }
    const relation = guarantor?.gurantor_relation || guarantor?.relation;
    if (relation) params.set("gurantor_relation", relation);
    navigate(`/guarantor/onboarding?${params.toString()}`);
  };

  useEffect(() => {
    if (
      haveValue(user?.gurantor_added_id) &&
      user?.gurantor_added_id?.length > 0 &&
      !reconnectFlow
    ) {
      let total_added = user?.gurantor_added_id?.length;
      let total_flagged =
        (user?.tenant_extra_field_id?.selected_onboarding_users?.filter(
          (item) => item?.type == "guarantor"
        )?.length ?? 0) + +(user?.gurantor_added_id?.length ?? 0);
      if (total_added === total_flagged) {
        setRedirectLoading(true);
        trackClarityEvent("tenant_guarantor_added");
        handleSubmitLastStep();
      }
    }
  }, []);

  const handleDoItLater = async () => {
    if (doItLaterLoading) {
      return;
    }
    // In dummy mode, just navigate without any API call
    if (isDummySignup) {
      setStep(8);
      return;
    }
    const formData = {
      role: localStorage.getItem("usertype"),
      lang: localStorage.getItem("i18nextLng"),
      step: 8,
    };

    try {
      setDoItLaterLoading(true);
      trackClarityEvent("tenant_guarantor_step_skipped");
      setTimeout(() => {
        trackClarityEvent("tenant_context_step_started");
      }, constants.CLARITY_DELAY);
      await updateUserProfile(formData);
      await loadUserTenant("tenant");
    } catch (err) {
    } finally {
      setDoItLaterLoading(false);
    }
  };

  const handleSubmitLastStep = async () => {
    try {
      const formData = {
        role: "tenant",
        step: 8,
      };
      await updateUserProfile(formData);
      trackClarityEvent("tenant_guarantor_step_ended");
      setTimeout(() => {
        trackClarityEvent("tenant_context_step_started");
      }, constants.CLARITY_DELAY);
    } catch (err) {
      console.log("Error", err);
    } finally {
      setRedirectLoading(false);
    }
  };

  if (redirectLoading) return <Loader />;

  const goBack = () => {
    if (usedFor === "popup") {
      setBankshow(false);
    } else {
      formHandler?.prev?.();
    }
  };

  const showSubmit =
    Array.isArray(user?.gurantor_added_id) &&
    user?.gurantor_added_id.length > 0 &&
    usedFor !== "popup";

  return (
    <Modal
      inline={inline}
      open
      onClose={goBack}
      dismissible={false}
      size="wide"
      onBack={goBack}
      backLabel={_t("goBack")}
      title={
        usedFor === "popup"
          ? _t(
              "updateYourFinancialInformation",
              "Text used at popup screen to connect bank"
            )
          : _t(
              "youCanCompleteAddingYour",
              "Text used in guarantor screen at onboarding flow"
            )
      }
      description={
        usedFor === "popup"
          ? _t(
              "completeAddingYourBankAccountForBetterExperience",
              "Text used at popup screen to connect bank"
            )
          : _t(
              "completeAddingYourGuarantorAndBankAccountForBetterExperience",
              "Text used in guarantor screen at onboarding flow"
            )
      }
      actionsLayout="end"
      actions={
        (usedFor !== "popup" || showSubmit) && (
          <>
            {usedFor !== "popup" && (
              <Button
                variant="secondary"
                size="lg"
                loading={doItLaterLoading}
                onClick={handleDoItLater}
                iconEnd={<Icon name="arrowRight" size={16} />}
              >
                {_t(
                  "iWillDoItLater",
                  "Text used in guarantor screen at onboarding flow"
                )}
              </Button>
            )}
            {showSubmit && (
              <Button
                size="lg"
                onClick={(e) => {
                  if (usedFor === "popup") {
                    setBankshow(false);
                  }
                }}
              >
                {_t("submit")}
              </Button>
            )}
          </>
        )
      }
    >
      {/* Sole switcher for guarantor-context screens. The top-level fixed
          switcher in OnboardingMain is hidden for guarantor flows
          (!isGuarantor guard), so these never show together. Gated on
          isGuarantor/usedFor=popup so this never doubles a top-level
          switcher in tenant contexts. */}
      {(isGuarantor || usedFor === "popup") && (
        <div className="guarantor-step__lang">
          <LanguageSwitch
            currentLang={currentLang}
            onChange={(lang) => i18n.changeLanguage(lang)}
          />
        </div>
      )}
      {!isGuarantor && usedFor !== "popup" && (
        <div className="guarantor-step__cards">
          {(!haveValue(
            user?.tenant_extra_field_id?.selected_onboarding_users
          ) ||
            user?.tenant_extra_field_id?.selected_onboarding_users?.filter(
              (item) => item?.type == "guarantor"
            )?.length === 0) &&
          (!haveValue(user?.gurantor_added_id) ||
            user?.gurantor_added_id?.length === 0) ? (
            <Card
              title={_t(
                "addAGuarantor",
                "Text used in guarantor screen at onboarding flow"
              )}
              description={_t(
                "guarantorHelpsToMakeYourProfileStrongIfYouHaveALowIncome",
                "Text used in guarantor screen at onboarding flow"
              )}
            >
              <p className="guarantor-step__text">
                {_t(
                  "youCanSenaAGuarantorRequestOrCreateAnAccountOnTheirBehalf",
                  "Text used in guarantor screen at onboarding flow"
                )}
              </p>
              <Button
                size="lg"
                block
                onClick={() => navigate("/guarantor/onboarding")}
                iconEnd={<Icon name="arrowRight" size={16} />}
              >
                {_t("addNow")}
              </Button>
            </Card>
          ) : (
            <Card
              title={_t(
                "addAGuarantor",
                "Text used in guarantor screen at onboarding flow"
              )}
              description={_t(
                "theseGuarantorsComeFromTheDocumentsYouHadProvidedEarlierCompleteAdding",
                "Text used in guarantor screen at onboarding flow"
              )}
              actions={
                <Badge tone="lime">
                  {user?.gurantor_added_id?.length ?? 0}/
                  {(user?.tenant_extra_field_id?.selected_onboarding_users?.filter(
                    (item) => item?.type == "guarantor"
                  )?.length ?? 0) +
                    +(user?.gurantor_added_id?.length ?? 0)}{" "}
                  {_t("completed")}
                </Badge>
              }
            >
              <div className="ds-option-list">
                {Array.isArray(
                  user?.tenant_extra_field_id?.selected_onboarding_users
                ) &&
                  user?.tenant_extra_field_id?.selected_onboarding_users
                    .filter((item) => item?.type == "guarantor")
                    .map((item, index) => (
                      <OptionCard
                        key={index}
                        icon={<Icon name="user" />}
                        title={item?.name}
                        onClick={() => openPendingGuarantor(item)}
                      />
                    ))}

                {Array.isArray(user?.gurantor_added_id) &&
                  user?.gurantor_added_id.map((item, index) => (
                    <OptionCard
                      key={index}
                      icon={<Icon name="user" />}
                      iconTone="muted"
                      title={`${item?.guarantor_id?.fname ?? ""} ${
                        item?.guarantor_id?.lname ?? ""
                      }`}
                      description={_t(
                        "pendingForApproval",
                        "Text used in guarantor screen at onboarding flow"
                      )}
                    >
                      <Badge tone="lime">{_t("added")}</Badge>
                    </OptionCard>
                  ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </Modal>
  );
};

export default GuarantorModal;
